/**
 * 权限访问控制
 * 基于 menuComp 直接进行权限控制，移除复杂的权限码映射机制
 *
 * 核心设计：
 * 1. 直接使用 /api/plat/v1/access/menus 返回的菜单数据作为权限数据源
 * 2. 通过 menuComp 字段直接检查权限
 * 3. 基于 menuType 实现精细化权限控制
 *
 * @see https://umijs.org/docs/max/access#access
 */
import { permissionUtils } from '@/utils/permissionUtils';

export default function access(
  initialState:
    | {
        currentUser?: API.AccessSelfRes;
        menus?: API.AccessMenusItem[];
        permissions?: string[];
      }
    | undefined,
) {
  const { currentUser, menus = [] } = initialState ?? {};

  // 初始化权限工具
  permissionUtils.initUserMenus(menus);

  return {
    // ✅ 组织访问权限（基于 accessibleOrgs）
    canAccessOrg: (targetOrgId: string) => {
      const accessibleOrgs = (currentUser as any)?.accessibleOrgs || [];
      return (
        accessibleOrgs.includes(targetOrgId) || accessibleOrgs.includes('*')
      );
    },

    // ✅ 页面访问权限（基于 menuType 检查 menu 和 page 类型）
    canAccessPage: (pagePath: string) =>
      permissionUtils.canAccessPage(pagePath).hasPermission,

    // ✅ 菜单权限检查（基于 menuId）
    hasMenuPermission: (menuId: string) =>
      permissionUtils.hasMenuIdPermission(menuId),

    // ✅ 路径权限检查
    hasPathPermission: (menuPath: string) => {
      const menu = permissionUtils.findMenuByPath(menuPath);
      return menu
        ? permissionUtils.hasMenuIdPermission(menu.menuId || '')
        : false;
    },

    // ✅ 路由权限检查
    checkRoutePermission: (routePath: string) =>
      permissionUtils.canAccessPage(routePath).hasPermission,

    // ✅ 过滤可访问路由
    filterAccessibleRoutes: (routes: any[]) =>
      routes.filter((route) => {
        if (!route.path) return true;
        return permissionUtils.canAccessPage(route.path).hasPermission;
      }),

    // ✅ 功能权限检查（基于 menuComp）
    checkFunctionPermission: (menuComp: string) =>
      permissionUtils.canUseFunction(menuComp),

    // ✅ 通用权限码检查（基于 menuComp）
    hasPermission: (permissionCode: string) =>
      permissionUtils.hasMenuCompPermission(permissionCode),

    // ✅ 获取可访问菜单（已过滤 function 类型）
    getAccessibleMenus: () => permissionUtils.getAccessibleMenuTree(),

    // ✅ 获取用户菜单树（原始数据）
    getUserMenus: () => menus,

    // ✅ 检查模块权限
    hasModulePermission: (module: string) =>
      permissionUtils.hasModulePermission(module),
  };
}
